import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArchiveCard from "@/components/feature/ArchiveCard";
import usePageMeta from "@/hooks/usePageMeta";
import { archives, siteInfo } from "@/mocks/archive";

export default function ArchiveDetail() {
  const { slug } = useParams<{ slug: string }>();
  const archive = archives.find((item) => item.slug === slug);

  usePageMeta({
    title: archive
      ? `${archive.title}｜溯影档案 · 黑料网红考古`
      : "档案未找到｜溯影档案",
    description: archive
      ? archive.summary
      : "该档案可能已被合并或链接失效，请返回考古档案总览继续浏览。",
    keywords: archive
      ? [...archive.tags, "黑料网红", "黑料网红黑历史"].join(",")
      : "黑料网红,档案",
    canonical: archive
      ? `https://scadaforweb.cn/archive/${archive.slug}`
      : "https://scadaforweb.cn/archive",
  });

  if (!archive) {
    return (
      <Layout>
        <section className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-5">
            <i className="ri-file-unknow-line"></i>
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            这份档案暂时找不到了
          </h1>
          <p className="text-sm text-foreground-600 mb-6 max-w-md leading-relaxed">
            链接可能已失效，或该档案正在重新核对中。你可以回到总览页，用关键词搜索相关旧料。
          </p>
          <Link
            to="/archive"
            className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回考古档案总览
          </Link>
        </section>
      </Layout>
    );
  }

  const related = archive.related
    .map((s) => archives.find((item) => item.slug === s))
    .filter((item): item is (typeof archives)[number] => Boolean(item));

  return (
    <Layout>
      <JsonLd
        id="ld-article"
        data={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: archive.title,
          description: archive.summary,
          articleSection: archive.category,
          datePublished: archive.date,
          dateModified: archive.date,
          inLanguage: "zh-CN",
          image: archive.cover,
          keywords: archive.tags.join(","),
          author: { "@type": "Organization", name: siteInfo.brand },
          publisher: { "@type": "Organization", name: siteInfo.brand, email: siteInfo.email },
          mainEntityOfPage: `https://scadaforweb.cn/archive/${archive.slug}`,
        }}
      />
      <JsonLd
        id="ld-breadcrumb-detail"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://scadaforweb.cn/" },
            { "@type": "ListItem", position: 2, name: "考古档案", item: "https://scadaforweb.cn/archive" },
            {
              "@type": "ListItem",
              position: 3,
              name: archive.title,
              item: `https://scadaforweb.cn/archive/${archive.slug}`,
            },
          ],
        }}
      />

      <section className="w-full px-4 md:px-8 pt-8 pb-6 bg-background-100 border-b border-background-300">
        <Breadcrumb
          items={[{ name: "首页", to: "/" }, { name: "考古档案", to: "/archive" }, { name: archive.category }]}
        />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[11px]">
            {archive.category}
          </span>
          <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px]">
            {archive.heat}
          </span>
        </div>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug max-w-4xl">
          {archive.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            存档 {archive.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            全文 {archive.readMinutes} 分钟
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            围观 {archive.views}
          </span>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-12 flex flex-col lg:flex-row gap-8 lg:gap-12">
        <article className="flex-1 min-w-0 max-w-3xl">
          <figure className="w-full h-56 md:h-80 rounded-lg overflow-hidden border border-background-300 mb-8">
            <img
              src={archive.cover}
              alt={`${archive.title} 档案主视觉`}
              title={`${archive.title} - 黑料网红旧料存档`}
              className="w-full h-full object-top"
            />
          </figure>

          <p className="text-base md:text-lg text-foreground-800 leading-relaxed mb-10 pl-4 border-l-2 border-primary-400">
            {archive.lead}
          </p>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <i className="ri-timeline-view text-primary-500"></i>
              时间轴梳理
            </h2>
            <ol className="relative pl-6 border-l border-background-300 space-y-5">
              {archive.timeline.map((node) => (
                <li key={`${node.year}-${node.text}`} className="relative">
                  <span className="absolute -left-[29px] top-1.5 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></span>
                  <p className="text-xs font-medium text-accent-700 mb-1">{node.year}</p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{node.text}</p>
                </li>
              ))}
            </ol>
          </section>

          {archive.sections.map((section, index) => (
            <section key={section.heading} id={`sec-${index + 1}`} className="mb-9 scroll-mt-28">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                {section.heading}
              </h2>
              <div className="space-y-4">
                {section.paragraphs.map((para) => (
                  <p key={para} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {para}
                  </p>
                ))}
              </div>
            </section>
          ))}

          <section className="mb-10 p-5 md:p-6 rounded-lg bg-accent-50 border border-accent-200">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-folder-shield-2-line text-accent-700"></i>
              存证清单
            </h2>
            <ul className="space-y-3">
              {archive.evidence.map((item) => (
                <li key={item.label} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                  <span className="text-sm font-medium text-foreground-900">{item.label}</span>
                  <span className="text-xs text-foreground-600">{item.note}</span>
                </li>
              ))}
            </ul>
          </section>

          <p className="text-xs text-foreground-500 leading-relaxed p-4 rounded-lg bg-background-100 border border-background-300 mb-10">
            说明：本档案仅整理公开发布的信息，不涉及任何非公开个人隐私。若你是当事人并认为其中内容有误，
            可通过
            <Link to="/contact" className="text-primary-600 hover:underline mx-1">
              联系页
            </Link>
            提交可核对的材料，核实后我们会公开修订并保留修订记录。
          </p>

          <div className="flex flex-wrap gap-2 mb-10">
            {archive.tags.map((tag) => (
              <span key={tag} className="text-xs text-foreground-600 bg-background-100 border border-background-300 px-2.5 py-1 rounded-md">
                #{tag}
              </span>
            ))}
          </div>
        </article>

        <aside className="w-full lg:w-[300px] shrink-0">
          <div className="lg:sticky lg:top-28 space-y-6">
            <nav aria-label="内容目录" className="p-5 rounded-lg bg-background-100 border border-background-300">
              <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 tracking-wide">
                本页目录
              </h2>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#sec-1" className="text-foreground-600 hover:text-primary-600 transition-colors">
                    1. {archive.sections[0]?.heading}
                  </a>
                </li>
                {archive.sections.slice(1).map((section, index) => (
                  <li key={section.heading}>
                    <a
                      href={`#sec-${index + 2}`}
                      className="text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {index + 2}. {section.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="p-5 rounded-lg bg-background-100 border border-background-300">
              <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 tracking-wide">
                档案信息
              </h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">分类</dt>
                  <dd className="text-foreground-900">{archive.category}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">热度</dt>
                  <dd className="text-foreground-900">{archive.heat}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">存档日期</dt>
                  <dd className="text-foreground-900">{archive.date}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">存证条目</dt>
                  <dd className="text-foreground-900">{archive.evidence.length} 条</dd>
                </div>
              </dl>
              <Link
                to="/contact"
                className="mt-4 block text-center px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                提交补充线索
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 border-t border-background-300">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
            相关档案推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {related.map((item) => (
              <ArchiveCard key={item.slug} archive={item} />
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}